// Export everything (/export) and a project's Export… in the Library: `.writingsuite` packages made in this browser
// (store/exporting.ts), one per project, downloaded as one zip with progress. The Mac imports each package with
// File ▸ Import ▸ Five Rooms Package (it merges, never replaces), so an export is also a way to leave with
// everything.
import { useEffect, useRef, useState } from 'react';
import { isOnline } from '../store/account.ts';
import { exportProjects, liveReader, saveDownload, type ExportProgress } from '../store/exporting.ts';
import { projectTitle, type LibraryProject } from '../store/library.ts';
import { announce, useApp } from '../shell/context.ts';
import { Masthead } from '../shell/Gate.tsx';
import { followLink } from '../shell/route.ts';

export interface ExportRun {
  busy: boolean;
  progress: ExportProgress | null;
  /** What happened, once it's done: what was saved, and anything left out. */
  result: { text: string; failed: string[] } | null;
  error: string | null;
  run(projects: LibraryProject[]): Promise<void>;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

/** Runs an export of some projects and keeps what the page shows about it. */
export function useExport(): ExportRun {
  const app = useApp();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [result, setResult] = useState<ExportRun['result']>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (projects: LibraryProject[]) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const deps = { api: app.account.session.api, store: app.account.store, online: isOnline, sessionEnded: app.sessionEnded };
      const r = await exportProjects(projects, liveReader(deps), setProgress);
      saveDownload(r.name, r.zip);
      const text = `Saved “${r.name}”: ${plural(r.packages.length, 'project')}, ${(r.zip.length / 1e6).toFixed(1)} MB.`;
      setResult({ text, failed: r.failed.map((f) => `“${f.title}”: ${f.reason}`) });
      announce(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };
  return { busy, progress, result, error, run };
}

export function progressText(p: ExportProgress): string {
  if (p.phase === 'zipping' || p.phase === 'done') return 'Putting the packages into one file…';
  const where = p.total > 1 ? ` (${p.index} of ${p.total})` : '';
  return p.phase === 'reading' ? `Reading “${p.title}”${where}…` : `Packing “${p.title}”${where}…`;
}

/** The progress bar and what's happening, or what happened. */
export function ExportStatus({ run }: { run: ExportRun }) {
  return (
    <>
      {run.progress ? (
        <div className="export-progress" data-export-progress>
          <progress max={1} value={run.progress.fraction} aria-label="Export progress" />
          <p className="status-note" role="status">
            {progressText(run.progress)}
          </p>
        </div>
      ) : null}
      {run.result ? (
        <div className="notice good" role="status" data-export-done>
          <p>{run.result.text}</p>
          {run.result.failed.length ? (
            <>
              <p>Left out:</p>
              <ul>
                {run.result.failed.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
      {run.error ? (
        <p className="error" role="alert">
          {run.error}
        </p>
      ) : null}
    </>
  );
}

export function ExportPage() {
  const app = useApp();
  const heading = useRef<HTMLHeadingElement>(null);
  const run = useExport();
  useEffect(() => {
    document.title = 'Export everything — Five Rooms';
    heading.current?.focus();
  }, []);
  const projects = app.projects ?? [];
  const offline = !app.online || app.listFromCache;
  return (
    <div className="calm-page">
      <Masthead signedIn />
      <main className="calm export-page" data-state="export">
        <h1 ref={heading} tabIndex={-1}>
          Export everything
        </h1>
        <p className="lede">
          Every project you’ve put on the web, each as a Five Rooms package: its documents with their whole history and checkpoints, in the same
          format the Mac and iPad use for backups. They’re made here, in this browser, because only your devices can open your projects.
        </p>
        <p>
          On a Mac, unzip the file and choose File ▸ Import ▸ Five Rooms Package for each one. Importing merges: nothing already on the Mac is
          lost.
        </p>
        {offline ? (
          <p className="notice" role="status">
            You’re offline, so each project is exported as this computer last had it, with any changes still waiting to be sent.
          </p>
        ) : null}
        {app.projects === null ? (
          <p className="lede">Opening your projects…</p>
        ) : projects.length === 0 ? (
          <p className="lede">There are no projects to export yet.</p>
        ) : (
          <>
            <ul className="export-list" aria-label="Projects to export">
              {projects.map((p) => (
                <li key={p.id}>{projectTitle(p)}</li>
              ))}
            </ul>
            <p className="actions">
              <button type="button" className="button primary" onClick={() => void run.run(projects)} disabled={run.busy} data-export-everything>
                {run.busy ? 'Exporting…' : `Export ${projects.length === 1 ? 'the project' : `all ${projects.length} projects`}`}
              </button>
              <a className="button secondary" href="/" onClick={(e) => followLink(e, '/')}>
                Back to the Library
              </a>
            </p>
          </>
        )}
        <ExportStatus run={run} />
      </main>
    </div>
  );
}
